import { useCallback, useEffect, useRef, useState } from "react";
import { Button, Input, Typography, message } from "antd";
import DeliveryPoster from "./DeliveryPoster";
import copyToClipboard from "../../utils/copy";
import { downloadDeliveryPosterFromElement, ensurePosterFontsLoaded } from "../../utils/deliveryPoster";
import { useTranslation } from "../../i18n";

const { Paragraph } = Typography;

function DeliverySharePanel({ url, albumName, expiresAtLabel = "", compact = false }) {
    const { t } = useTranslation();
    const posterRef = useRef(null);
    const [posterReady, setPosterReady] = useState(false);
    const [posterError, setPosterError] = useState("");

    useEffect(() => {
        if (!url) {
            setPosterReady(false);
            setPosterError("");
            return;
        }

        setPosterReady(false);
        setPosterError("");

        let cancelled = false;

        async function preparePoster() {
            try {
                await ensurePosterFontsLoaded();
                if (!cancelled) {
                    setPosterReady(true);
                }
            } catch (error) {
                console.error("Poster font loading failed:", error);
                if (!cancelled) {
                    setPosterReady(true);
                }
            }
        }

        preparePoster();

        return () => {
            cancelled = true;
        };
    }, [url, albumName, expiresAtLabel]);

    const handleCopy = useCallback(async () => {
        if (!url) {
            return;
        }
        try {
            await copyToClipboard(url);
            message.success(t("delivery.share.linkCopied"));
        } catch {
            message.error(t("delivery.share.copyFailed"));
        }
    }, [url, t]);

    const handleDownloadPoster = useCallback(async () => {
        if (!url || !posterRef.current) {
            return;
        }
        try {
            await downloadDeliveryPosterFromElement(posterRef.current, albumName);
            message.success(t("delivery.share.posterDownloaded"));
        } catch (error) {
            console.error("Poster download failed:", error);
            setPosterError(t("delivery.share.posterFailed"));
            message.error(t("delivery.share.posterDownloadFailed"));
        }
    }, [url, albumName, t]);

    return (
        <div className={`delivery-share${compact ? " delivery-share--compact" : ""}`}>
            {!compact && (
                <div className="delivery-share__header">
                    <h2 className="delivery-share__album">{albumName}</h2>
                    <p className="delivery-share__subtitle">{t("delivery.share.subtitle")}</p>
                    <p className="delivery-share__subtitle-en">{t("delivery.share.subtitleEn")}</p>
                </div>
            )}

            <div className="delivery-share__url-row">
                <Input readOnly value={url} aria-label={t("delivery.share.urlLabel")} />
                <Button type="primary" onClick={handleCopy}>
                    {t("delivery.share.copyLink")}
                </Button>
            </div>

            <div className="delivery-share__actions">
                <Button
                    onClick={handleDownloadPoster}
                    disabled={!posterReady || Boolean(posterError)}
                >
                    {t("delivery.share.downloadPoster")}
                </Button>
            </div>

            <div className="delivery-share__poster-wrap">
                {!posterReady && url && (
                    <p className="delivery-share__poster-status">{t("delivery.share.posterLoading")}</p>
                )}
                {posterError && (
                    <Paragraph type="danger" className="delivery-share__poster-status">
                        {posterError}
                    </Paragraph>
                )}
                {url && (
                    <div className={posterReady ? "" : "delivery-share__poster--loading"}>
                        <DeliveryPoster
                            ref={posterRef}
                            url={url}
                            albumName={albumName}
                            expiresAtLabel={expiresAtLabel}
                        />
                    </div>
                )}
            </div>


        </div>
    );
}

export default DeliverySharePanel;
